import { ProjectStatus } from "types/project.types";
import type { ProjectFilters } from "./components/FilterBar/filterStorage";

/** The Projects tab of Project Management. */
export const COMPANY_PROJECTS_PATH = "/workflow/projects";

/** The statuses a company's work is still open in (EQ-2485). */
const OPEN_PROJECT_STATUSES = [
	ProjectStatus.New,
	ProjectStatus["In progress"],
	ProjectStatus["On hold"],
];

/**
 * The dropdown filters a navigation can preset. The search box is left alone:
 * it is the user's own typing, shown in plain sight.
 */
export type ProjectFilterPreset = Omit<ProjectFilters, "title">;

/**
 * Marks a Projects tab the Company Projects row opened, and for which
 * company, so the sidebar highlights that row there (EQ-2488).
 */
interface CompanyProjectsEntry {
	companyProjects: number;
}

interface CompanyProjectsState extends CompanyProjectsEntry {
	projectFilters: ProjectFilterPreset;
}

/**
 * Where the sidebar's Company Projects row goes (EQ-2485): the Projects tab
 * showing the company's New, In Progress and On Hold projects of every type,
 * with no assignee filter. The filters ride in router state, so the tab
 * applies them on arrival even when it is already open, and the address bar
 * stays the plain tab path. The state also marks the entry (EQ-2488).
 */
export function companyProjectsLink(companyId: number): {
	to: string;
	state: CompanyProjectsState;
} {
	return {
		to: COMPANY_PROJECTS_PATH,
		state: {
			companyProjects: companyId,
			projectFilters: {
				company: [companyId],
				member: [],
				status: [...OPEN_PROJECT_STATUSES],
				kind: null,
			},
		},
	};
}

const isIdList = (value: unknown): value is number[] =>
	Array.isArray(value) &&
	value.every((id) => typeof id === "number" && Number.isFinite(id));

/**
 * The filter preset a navigation carried, or null when it carried none.
 * Router state survives a reload and can come from any page, so it is checked
 * rather than trusted. A preset that is there but malformed is reported, since
 * the tab then shows the locked filters instead of what the link asked for.
 */
export function readProjectFilterPreset(
	state: unknown,
): ProjectFilterPreset | null {
	if (!state || typeof state !== "object" || !("projectFilters" in state)) {
		return null;
	}
	const preset = (state as { projectFilters: unknown }).projectFilters;
	const fields = (preset ?? {}) as Partial<Record<string, unknown>>;
	const kind = fields.kind ?? null;
	if (
		typeof preset !== "object" ||
		!isIdList(fields.company) ||
		!isIdList(fields.member) ||
		!isIdList(fields.status) ||
		(kind !== null && kind !== "project" && kind !== "close")
	) {
		console.warn("Ignoring a malformed project filter preset", preset);
		return null;
	}
	return {
		company: fields.company,
		member: fields.member,
		status: fields.status,
		kind,
	};
}

/**
 * The company a Company Projects click opened the Projects tab for, or null
 * when the tab was opened any other way (EQ-2488). The sidebar highlights the
 * row, not Project Management, while this is set.
 */
export function readCompanyProjectsEntry(state: unknown): number | null {
	if (!state || typeof state !== "object" || !("companyProjects" in state)) {
		return null;
	}
	const companyId = (state as CompanyProjectsEntry).companyProjects;
	return typeof companyId === "number" && Number.isFinite(companyId)
		? companyId
		: null;
}

/**
 * Whether this is the Projects tab the Company Projects row opened for the
 * company the sidebar shows (EQ-2488). Both sidebar menus decide their
 * highlight from it, so exactly one of them highlights.
 */
export function isCompanyProjectsTab(
	location: { pathname: string; state: unknown },
	companyId: number | null,
): boolean {
	return (
		companyId !== null &&
		location.pathname === COMPANY_PROJECTS_PATH &&
		readCompanyProjectsEntry(location.state) === companyId
	);
}

/**
 * What stays in history once the tab has applied a Company Projects preset:
 * the entry marker without the filters, so a reload or Back keeps the
 * highlight but not the preset.
 */
export function companyProjectsEntryState(
	state: unknown,
): CompanyProjectsEntry | null {
	const companyId = readCompanyProjectsEntry(state);
	return companyId === null ? null : { companyProjects: companyId };
}
